import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Plus, Pencil, Trash2, Mail, StickyNote, Users, Search, Phone, MessageCircle, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

export const CATEGORIAS = [
  { id: "aseguradora", label: "Aseguradora" },
  { id: "gestoria", label: "Gestoría" },
  { id: "veterinario", label: "Veterinario" },
  { id: "clinica", label: "Clínica" },
  { id: "proveedor", label: "Proveedor" },
  { id: "comunidad", label: "Comunidad" },
  { id: "otros", label: "Otros" },
];
const catLabel = (id) => CATEGORIAS.find((c) => c.id === id)?.label || "Otros";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const telOk = (t) => /^\+?[\d\s().-]{6,25}$/.test(t.trim()) && (t.replace(/\D/g, "").length >= 6 && t.replace(/\D/g, "").length <= 15);
const telLlamar = (t) => (t.trim().startsWith("+") ? "+" : "") + t.replace(/\D/g, "");
// WhatsApp necesita prefijo internacional: si es un número español sin prefijo, se añade 34.
const telWhatsApp = (t) => { const d = t.replace(/\D/g, ""); return t.trim().startsWith("+") || t.trim().startsWith("00") ? d.replace(/^00/, "") : d.length === 9 ? `34${d}` : d; };

const TelefonoAcciones = ({ c }) => (
  <div className="mt-1">
    <p className="text-sm text-[#1C2421] font-semibold" data-testid={`contacto-telefono-${c.id}`}>{c.telefono}</p>
    <div className="flex flex-wrap gap-2 mt-2">
      <a data-testid={`contacto-llamar-${c.id}`} href={`tel:${telLlamar(c.telefono)}`} className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-lg bg-[#3A6B56] text-white text-sm font-semibold"><Phone size={15} /> Llamar</a>
      <a data-testid={`contacto-whatsapp-${c.id}`} href={`https://wa.me/${telWhatsApp(c.telefono)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-lg border-2 border-[#3A6B56] text-[#3A6B56] text-sm font-semibold"><MessageCircle size={15} /> WhatsApp</a>
    </div>
  </div>
);

export default function Contactos() {
  const navigate = useNavigate();
  const { contactos, crearContacto, actualizarContacto, eliminarContacto } = useData();
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState(params.get("nuevo") === "1" ? {} : null); // null | {} (nuevo) | contacto
  useEffect(() => { if (params.get("nuevo") === "1") { setEditing({}); setParams({}, { replace: true }); } }, [params, setParams]);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const filtrados = !q ? (contactos || []) : (contactos || []).filter((c) =>
    [c.nombre, c.email, c.telefono, c.direccion, catLabel(c.categoria), c.categoria].some((v) => (v || "").toLowerCase().includes(q))
  );

  return (
    <div className="space-y-5">
      <header className="aldia-fade-up flex items-center gap-3">
        <button data-testid="contactos-back" onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/perfil"))}
          className="shrink-0 w-10 h-10 rounded-full bg-white border border-[#E5DFD5] flex items-center justify-center hover:border-[#3A6B56]">
          <ChevronLeft size={22} className="text-[#3A6B56]" />
        </button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C2421]">Agenda de contactos</h1>
          <p className="text-base text-[#6E7A74] mt-0.5">Tus contactos frecuentes para preparar correos.</p>
        </div>
      </header>

      <button
        data-testid="contacto-nuevo-btn"
        onClick={() => setEditing({})}
        className="w-full min-h-[54px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base hover:bg-[#2E5645] transition-colors flex items-center justify-center gap-2"
      >
        <Plus size={20} /> Añadir contacto
      </button>

      {(contactos || []).length > 0 && (
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9AA39D]" />
          <input
            data-testid="contactos-buscar"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre, email o categoría…"
            className="w-full min-h-[52px] pl-11 pr-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56]"
          />
        </div>
      )}

      {(contactos || []).length === 0 ? (
        <div className="py-14 flex flex-col items-center gap-3 text-center" data-testid="contactos-vacio">
          <Users size={40} className="text-[#C4BDB0]" />
          <p className="text-base text-[#6E7A74]">Aún no tienes contactos guardados.</p>
        </div>
      ) : filtrados.length === 0 ? (
        <div className="py-12 flex flex-col items-center gap-2 text-center" data-testid="contactos-sin-resultados">
          <Search size={32} className="text-[#C4BDB0]" />
          <p className="text-base text-[#6E7A74]">Ningún contacto coincide con «{query}».</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="contactos-list">
          {filtrados.map((c) => (
            <div key={c.id} data-testid={`contacto-item-${c.id}`}
              className="bg-white border border-[#E5DFD5] rounded-2xl p-4 shadow-sm flex items-start gap-3">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-[#E8F0EC] flex items-center justify-center">
                <Mail size={20} className="text-[#3A6B56]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading font-bold text-base text-[#1C2421]" data-testid={`contacto-nombre-${c.id}`}>{c.nombre || "Sin nombre"}</h3>
                  <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-[#F2EDE4] text-[#6E7A74]">{catLabel(c.categoria)}</span>
                </div>
                {c.email && <p className="text-sm text-[#6E7A74] truncate" data-testid={`contacto-email-${c.id}`}>{c.email}</p>}
                {c.direccion && <p className="text-sm text-[#4A5550] mt-1 flex items-start gap-1.5" data-testid={`contacto-direccion-${c.id}`}><MapPin size={14} className="mt-0.5 shrink-0 text-[#9AA39D]" />{c.direccion}</p>}
                {c.telefono && <TelefonoAcciones c={c} />}
                {c.notas && <p className="text-sm text-[#4A5550] mt-1 flex items-start gap-1.5"><StickyNote size={14} className="mt-0.5 shrink-0 text-[#9AA39D]" />{c.notas}</p>}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button data-testid={`contacto-editar-${c.id}`} onClick={() => setEditing(c)}
                  aria-label="Editar" className="w-9 h-9 rounded-lg bg-white border border-[#E5DFD5] flex items-center justify-center hover:border-[#3A6B56]">
                  <Pencil size={16} className="text-[#3A6B56]" />
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`contacto-borrar-${c.id}`} aria-label="Eliminar"
                      className="w-9 h-9 rounded-lg bg-white border border-[#FECACA] flex items-center justify-center hover:bg-[#FEF2F2]">
                      <Trash2 size={16} className="text-[#B91C1C]" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#FAF8F5] border-[#E5DFD5]">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-[#1C2421]">¿Eliminar contacto?</AlertDialogTitle>
                      <AlertDialogDescription className="text-[#6E7A74]">
                        Se eliminará «{c.nombre || "este contacto"}». Esta acción no se puede deshacer.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        data-testid={`contacto-borrar-confirm-${c.id}`}
                        className="bg-[#B91C1C] hover:bg-[#991B1B]"
                        onClick={async () => {
                          try { await eliminarContacto(c.id); toast.success("Contacto eliminado"); }
                          catch { toast.error("No se pudo eliminar."); }
                        }}
                      >
                        Eliminar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      <ContactoForm
        editing={editing}
        onClose={() => setEditing(null)}
        crearContacto={crearContacto}
        actualizarContacto={actualizarContacto}
      />
    </div>
  );
}

const soloDigitos = (t) => (t || "").replace(/\D/g, "").slice(-9);
const buscarDuplicado = (lista, p) => (lista || []).find((c) =>
  (p.telefono && soloDigitos(c.telefono) && soloDigitos(c.telefono) === soloDigitos(p.telefono)) ||
  (p.email && (c.email || "").toLowerCase() === p.email.toLowerCase()));

export const ContactoForm = ({ editing, onClose, crearContacto, actualizarContacto, comprobarDuplicados = false, onSaved }) => {
  const { contactos } = useData();
  const [duplicado, setDuplicado] = useState(null);
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [direccion, setDireccion] = useState("");
  const [categoria, setCategoria] = useState("otros");
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (editing) {
      setNombre(editing.nombre || "");
      setEmail(editing.email || "");
      setTelefono(editing.telefono || "");
      setDireccion(editing.direccion || "");
      setCategoria(editing.categoria || "otros");
      setNotas(editing.notas || "");
      setSaving(false);
      setDuplicado(null);
    }
  }, [editing]);

  if (!editing) return null;
  const esNuevo = !editing.id;
  const emailOk = !email.trim() || EMAIL_RE.test(email.trim());

  const guardar = async (modo = "") => {
    if (![nombre, email, telefono, direccion, notas].some((v) => v.trim())) return toast.error("Añade al menos un dato para guardar el contacto.");
    if (!emailOk) return toast.error("Escribe un email válido.");
    if (telefono.trim() && !telOk(telefono)) return toast.error("Escribe un teléfono válido (por ejemplo +34 600 123 456).");
    const payload = { nombre: nombre.trim(), email: email.trim(), telefono: telefono.trim(), direccion: direccion.trim(), categoria, notas: notas.trim() };
    const dup = esNuevo && comprobarDuplicados && !modo ? buscarDuplicado(contactos, payload) : null;
    if (dup) return setDuplicado(dup);
    setSaving(true);
    let guardadoId = null;
    try {
      if (modo === "actualizar") {
        // Completa el contacto existente con los datos nuevos, sin perder sus notas.
        const d = duplicado, nuevo = Object.fromEntries(Object.entries(payload).filter(([k, x]) => x && k !== "categoria" && k !== "notas"));
        const lineas = (d.notas || "").split("\n").filter(Boolean);
        const clave = (l) => (l.includes(":") ? l.split(":")[0].trim().toLowerCase() : l.trim().toLowerCase());
        (payload.notas || "").split("\n").filter(Boolean).forEach((l) => { if (!lineas.some((x) => clave(x) === clave(l))) lineas.push(l); });
        const notasJuntas = lineas.join("\n");
        await actualizarContacto(d.id, { ...nuevo, notas: notasJuntas }); toast.success(`«${d.nombre || "Contacto"}» actualizado`);
        guardadoId = d.id;
      } else if (esNuevo) { guardadoId = (await crearContacto(payload))?.id; toast.success("Contacto guardado"); }
      else { await actualizarContacto(editing.id, payload); toast.success("Contacto actualizado"); guardadoId = editing.id; }
      onClose();
      if (onSaved) onSaved(guardadoId);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo guardar el contacto.");
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}
        className="bg-[#FAF8F5] border-[#E5DFD5] max-w-md top-[3dvh] translate-y-0 max-h-[94dvh] overflow-y-auto overscroll-contain data-[state=open]:slide-in-from-top-0 sm:top-[50%] sm:translate-y-[-50%]" data-testid="contacto-form">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#1C2421]">{esNuevo ? "Nuevo contacto" : "Editar contacto"}</DialogTitle>
          <DialogDescription className="text-[#6E7A74]" data-testid="contacto-form-descripcion">Guarda los datos que quieras tener a mano.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">Nombre <span className="font-normal text-[#9AA39D]">(opcional)</span></label>
            <input data-testid="contacto-nombre-input" value={nombre} onChange={(e) => setNombre(e.target.value)}
              placeholder="Mapfre, Veterinario Toby…"
              className="w-full min-h-[52px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56]" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">Email <span className="font-normal text-[#9AA39D]">(opcional)</span></label>
            <input data-testid="contacto-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="correo@ejemplo.com"
              className={`w-full min-h-[52px] px-4 rounded-xl bg-white border text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56] ${email && !emailOk ? "border-[#FCA5A5]" : "border-[#E5DFD5]"}`} />
          </div>
          <div>
            <label htmlFor="contacto-telefono" className="block text-sm font-semibold text-[#4A5550] mb-1.5">Teléfono <span className="font-normal text-[#9AA39D]">(opcional)</span></label>
            <input id="contacto-telefono" data-testid="contacto-telefono-input" type="tel" inputMode="tel" autoComplete="tel" maxLength={30} value={telefono} onChange={(e) => setTelefono(e.target.value)}
              placeholder="+34 600 123 456"
              className={`w-full min-h-[52px] px-4 rounded-xl bg-white border text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56] ${telefono.trim() && !telOk(telefono) ? "border-[#FCA5A5]" : "border-[#E5DFD5]"}`} />
          </div>
          <div>
            <label htmlFor="contacto-direccion" className="block text-sm font-semibold text-[#4A5550] mb-1.5">Dirección <span className="font-normal text-[#9AA39D]">(opcional)</span></label>
            <textarea id="contacto-direccion" data-testid="contacto-direccion-input" rows={2} maxLength={300} value={direccion} onChange={(e) => setDireccion(e.target.value)}
              placeholder="Calle, número, localidad"
              className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56] resize-y" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">Categoría</label>
            <select data-testid="contacto-categoria-select" value={categoria} onChange={(e) => setCategoria(e.target.value)}
              className="w-full min-h-[52px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56]">
              {CATEGORIAS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">Notas (opcional)</label>
            <textarea data-testid="contacto-notas-input" value={notas} onChange={(e) => setNotas(e.target.value)} rows={3}
              className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56] resize-y" />
          </div>
          {duplicado && (
            <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-3 space-y-2" data-testid="contacto-duplicado">
              <p className="text-base text-[#9A3412] font-semibold">Ya tienes «{duplicado.nombre || duplicado.telefono || duplicado.email}» con ese teléfono o correo.</p>
              <button data-testid="contacto-duplicado-actualizar" onClick={() => guardar("actualizar")} disabled={saving}
                className="w-full min-h-[48px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base">Actualizar ese contacto</button>
              <button data-testid="contacto-duplicado-nuevo" onClick={() => guardar("nuevo")} disabled={saving}
                className="w-full min-h-[48px] rounded-xl bg-white border border-[#D6CFC2] text-[#1C2421] font-semibold text-base">Crear uno nuevo igualmente</button>
            </div>
          )}
          {!duplicado && <button data-testid="contacto-guardar-btn" onClick={() => guardar()} disabled={saving}
            className="w-full min-h-[54px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base hover:bg-[#2E5645] transition-colors disabled:opacity-60">
            {saving ? "Guardando…" : esNuevo ? "Guardar contacto" : "Guardar cambios"}
          </button>}
          <button data-testid="contacto-cancelar-btn" onClick={onClose} disabled={saving}
            className="w-full min-h-[50px] rounded-xl bg-white border border-[#D6CFC2] text-[#1C2421] font-semibold text-base disabled:opacity-60">
            Cancelar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
